'use client'

import type { CellContext, ColumnDef, ColumnDefTemplate, SortingFn } from '@tanstack/react-table'
import { useMemo } from 'react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Avatar, AvatarFallback, AvatarImage, DataTableColumnHeader } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import { formatSecondsToMinutes, getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useAuthClient } from '../../../context'
import { countStars, isStarredSession } from './count-stars'

export interface GetColumnsProps {
  type: 'school' | 'web'
  renderLink: RenderLink
}

type Data = RouterOutputs['metrics']['getReadingJournal']['journals'][number]

function sortingFnFactory(day: number): SortingFn<Data> {
  return (rowA, rowB) => {
    const a = rowA.original.sessions.find((s) => s.dayOfWeek === day)?.totalDurationSeconds ?? 0
    const b = rowB.original.sessions.find((s) => s.dayOfWeek === day)?.totalDurationSeconds ?? 0
    return a - b
  }
}

function cellContentFactory(day: number): ColumnDefTemplate<CellContext<Data, unknown>> {
  return ({ row }) => {
    const findDate = row.original.sessions.find((session) => session.dayOfWeek === day)

    if (!findDate) return <div className='srd:text-nowrap'>-</div>

    const totalSeconds = findDate.totalDurationSeconds

    const str = formatSecondsToMinutes(totalSeconds)

    if (isStarredSession(totalSeconds))
      return (
        <div className='srd:text-nowrap srd:flex srd:items-center srd:gap-x-2'>
          <img
            src={getStaticAssetUrl('illustration/bintang-senyum.svg')}
            alt='bintang senyum'
            width={24}
            height={24}
            draggable={false}
          />

          <span className={cn(typographyVariants({ variant: 'body-lg' }))}>{str}</span>
        </div>
      )

    return <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>{str}</div>
  }
}

export function useColumns({ type, renderLink }: GetColumnsProps): ColumnDef<Data>[] {
  const authClient = useAuthClient()

  const { data: session } = authClient.useSession()

  return useMemo(
    () => [
      {
        id: 'id',
        accessorKey: 'Nama',
        filterFn: (row, _columnId, filterValue: string) =>
          row.original.name.toLowerCase().includes(filterValue.toLowerCase()),
        sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Nama' />,
        cell: ({ row }) => {
          return (
            <div className='srd:flex srd:items-center srd:gap-x-2 srd:flex-nowrap srd:shrink-0 srd:w-[250px] srd:truncate'>
              <Avatar profile='user' className='srd:size-10'>
                <AvatarImage src={row.original.image ?? undefined} />
                <AvatarFallback>{getInitials(row.original.name)}</AvatarFallback>
              </Avatar>

              {renderLink({
                children: row.original.name,
                className: cn(typographyVariants({ variant: 'body-lg' }), 'srd:hover:underline'),
                meta: {
                  childId: row.original.id,
                  back_redirect: 'reading-journal',
                },
              })}
            </div>
          )
        },
      },

      {
        id: 'total',
        accessorKey: 'Total',
        sortingFn: (rowA, rowB) => countStars(rowA.original) - countStars(rowB.original),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Total ★' />,
        cell: ({ row }) => {
          const total = countStars(row.original)

          if (total === 0)
            return <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>-</div>

          return <div className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-nowrap')}>{total}</div>
        },
      },

      {
        id: 'senin',
        accessorKey: 'Senin',
        sortingFn: sortingFnFactory(1),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Senin' />,
        cell: cellContentFactory(1),
      },

      {
        id: 'selasa',
        accessorKey: 'Selasa',
        sortingFn: sortingFnFactory(2),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Selasa' />,
        cell: cellContentFactory(2),
      },

      {
        id: 'rabu',
        accessorKey: 'Rabu',
        sortingFn: sortingFnFactory(3),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Rabu' />,
        cell: cellContentFactory(3),
      },

      {
        id: 'kamis',
        accessorKey: 'Kamis',
        sortingFn: sortingFnFactory(4),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Kamis' />,
        cell: cellContentFactory(4),
      },

      {
        id: 'jumat',
        accessorKey: 'Jumat',
        sortingFn: sortingFnFactory(5),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Jumat' />,
        cell: cellContentFactory(5),
      },

      {
        id: 'sabtu',
        accessorKey: 'Sabtu',
        sortingFn: sortingFnFactory(6),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Sabtu' />,
        cell: cellContentFactory(6),
      },

      {
        id: 'minggu',
        accessorKey: 'Minggu',
        sortingFn: sortingFnFactory(0),
        header: ({ column }) => <DataTableColumnHeader column={column} title='Minggu' />,
        cell: cellContentFactory(0),
      },
    ],
    [type, renderLink, session],
  )
}
